import React, { useEffect, useState } from "react";
import API_BASE_URL from "../../config/apiConfig";
import { useAuth } from "../../context/AuthProvider";
import WebmasterSidebar from "../../components/WebmasterSidebar";
import avatar from "../../assets/images/icon-avatar.svg";
import ExportMedicScores from "../../components/ExportMedicScores";

const intervalOptions = [
  { label: "Săptămâna curentă", value: "this_week" },
  { label: "Săptămâna trecută", value: "last_week" },
  { label: "Luna curentă", value: "this_month" },
  { label: "Luna trecută", value: "last_month" },
  { label: "Interval custom", value: "custom" },
];

const sortOptions = [
  { label: "Consultații (desc)", value: "consult_units" },
  { label: "Acoperire program (desc)", value: "coverage_percent" },
];

const getStartEndDates = (type: string) => {
  const today = new Date();
  let start: Date, end: Date;
  const getMondayOfWeek = (date: Date) => {
    const c = new Date(date.getTime());
    const day = c.getDay();
    const diff = day === 0 ? -6 : 1 - day;
    c.setDate(c.getDate() + diff);
    c.setHours(0, 0, 0, 0);
    return c;
  };
  if (type === "this_week") {
    start = getMondayOfWeek(today);
    end = new Date(start); end.setDate(start.getDate() + 6);
  } else if (type === "last_week") {
    const lw = new Date(today); lw.setDate(today.getDate() - 7);
    start = getMondayOfWeek(lw);
    end = new Date(start); end.setDate(start.getDate() + 6);
  } else if (type === "this_month") {
    start = new Date(today.getFullYear(), today.getMonth(), 1);
    end = new Date(today.getFullYear(), today.getMonth() + 1, 0);
  } else if (type === "last_month") {
    const year = today.getMonth() === 0 ? today.getFullYear() - 1 : today.getFullYear();
    const month = today.getMonth() === 0 ? 11 : today.getMonth() - 1;
    start = new Date(year, month, 1);
    end = new Date(year, month + 1, 0);
  } else { start = today; end = today; }
  const format = (d: Date, t: "start" | "end") => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}${t === "start" ? "T00:00:00" : "T23:59:59.999"}`;
  };
  return { start_time: format(start, "start"), end_time: format(end, "end") };
};

interface ConsultBreakdown {
  count: number;
}

interface MedicScoreData {
  therapist_id: string;
  consult_breakdown: Record<string, ConsultBreakdown>;
  consult_units: number;
  norma_slots: number;
  coverage_percent: number;
  vacation_days: number;
}

interface GlobalStats {
  coverage_avg: number;
  total_medics: number;
  medics_with_norma: number;
  total_consult_units: number;
  total_norma_slots: number;
}

interface Medic {
  user_id: string;
  firstname: string;
  lastname: string;
  is_active: boolean;
}

interface Item { key: string; value: string; }

const MedicScoreTable: React.FC = () => {
  const { token } = useAuth();
  const [scoreData, setScoreData] = useState<MedicScoreData[]>([]);
  const [globalStats, setGlobalStats] = useState<GlobalStats | null>(null);
  const [medics, setMedics] = useState<Medic[]>([]);
  const [typeMap, setTypeMap] = useState<Record<string, string>>({});
  const [intervalType, setIntervalType] = useState("this_week");
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");
  const [sortBy, setSortBy] = useState("consult_units");
  const [isCollapsed, setIsCollapsed] = useState(
    () => localStorage.getItem("sidebarCollapsed") === "true"
  );

  useEffect(() => {
    if (!token) return;
    const fetchAll = async () => {
      try {
        const [medicRes, actRes, thRes, consRes, medActRes] = await Promise.all([
        fetch(`${API_BASE_URL}/users/medics`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API_BASE_URL}/types/activities`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API_BASE_URL}/types/therapy-type`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API_BASE_URL}/types/consult-types`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API_BASE_URL}/types/medical-activities`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      const mData = await medicRes.json();
      const acData = await actRes.json();
      const thData = await thRes.json();
      const consData = await consRes.json();
      const medActData = await medActRes.json();

      setMedics(Array.isArray(mData) ? mData : mData?.data || []);

      const map: Record<string, string> = {};
      [
        ...(acData.data || []),
        ...(thData.data || []),
        ...(consData.data || []),
        ...(medActData.data || []),
      ].forEach((i: Item) => (map[i.key] = i.value));
      setTypeMap(map);
      } catch (err) {
        console.error("Eroare la încărcare medici/tipuri:", err);
      }
    };
    fetchAll();
  }, [token]);
  

  const fetchScores = async () => {
    if (!token) return;
    let start_time = "", end_time = "";
    if (intervalType === "custom") {
      if (!customStart || !customEnd) return;
      start_time = `${customStart}T00:00:00`;
      end_time = `${customEnd}T23:59:59.999`;
    } else {
      const r = getStartEndDates(intervalType);
      start_time = r.start_time; end_time = r.end_time;
    }
    const params = new URLSearchParams({ start_time, end_time });
    const url = `${API_BASE_URL}/medic-score-config/total-by-medic?${params}`;
    try {
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) return;
      const json = await res.json();
      setScoreData(json?.data || []);
      setGlobalStats(json?._global || null);
    } catch (err) {
      console.error("Fetch failed:", err);
    }
  };

  const getSelectedRangeISO = () => {
    if (intervalType === "custom") {
      if (!customStart || !customEnd) return null;
      return {
        start_time: `${customStart}T00:00:00`,
        end_time: `${customEnd}T23:59:59.999`,
      };
    }
    return getStartEndDates(intervalType);
  };
  const range = getSelectedRangeISO();

  useEffect(() => {
    fetchScores();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, intervalType, customStart, customEnd]);

  const scoreByMedic: Record<string, MedicScoreData> = {};
  for (const row of scoreData) scoreByMedic[row.therapist_id] = row;

  const activeMedics = medics.filter((m) => m.is_active);

  const getSortValue = (uid: string): number => {
    const row = scoreByMedic[uid];
    if (!row) return 0;
    switch (sortBy) {
      case "consult_units": return row.consult_units || 0;
      case "coverage_percent": return row.coverage_percent || 0;
      default: return row.consult_units || 0;
    }
  };

  const withValue = [...activeMedics]
    .filter((m) => getSortValue(m.user_id) > 0)
    .sort((a, b) => getSortValue(b.user_id) - getSortValue(a.user_id));
  const zeroValue = activeMedics.filter((m) => getSortValue(m.user_id) === 0);
  const ordered = [...withValue, ...zeroValue];

  const coverageColor = (pct: number) => {
    if (pct >= 75) return "#059669";
    if (pct >= 50) return "#d97706";
    return "#dc2626";
  };

  return (
    <div className={`dashboard-container ${isCollapsed ? "collapsed" : ""}`}>
      <WebmasterSidebar onCollapseChange={setIsCollapsed} />
      <div className="page-content">
        <h1 className="page-title">Consultații & Acoperire Medici</h1>

        <div className="interval-container">
          <label>Interval:</label>
          <select value={intervalType} onChange={(e) => setIntervalType(e.target.value)}>
            {intervalOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          {intervalType === "custom" && (
            <>
              <input type="date" value={customStart} onChange={(e) => setCustomStart(e.target.value)} />
              <input type="date" value={customEnd} onChange={(e) => setCustomEnd(e.target.value)} />
            </>
          )}
          <label style={{ marginLeft: 16 }}>Sortare:</label>
          <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
            {sortOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          {range && (
          <div style={{ marginLeft: 12 }}>
            <ExportMedicScores
              start_time={range.start_time}
              end_time={range.end_time}
            />
          </div>
)}
        </div>

        {/* ═══ BANNER ═══ */}
        {globalStats && (
          <div className="global-stats-banner">
            <div className="global-stat-main">
              <div className="global-stat-label">Acoperire program medici (media)</div>
              <div
                className="global-stat-value"
                style={{ color: coverageColor(globalStats.coverage_avg) }}
              >
                {globalStats.coverage_avg}%
              </div>
              <div className="global-stat-detail">
                {globalStats.total_consult_units} / {globalStats.total_norma_slots} sloturi (total)
              </div>
            </div>
            <div className="global-stat-side">
              <div className="global-stat-item">
                <span className="gs-value">{globalStats.total_medics}</span>
                <span className="gs-label">medici activi</span>
              </div>
              <div className="global-stat-item">
                <span className="gs-value">{globalStats.total_consult_units}</span>
                <span className="gs-label">consultații efectuate</span>
              </div>
            </div>
          </div>
        )}

        {/* ═══ Cards ═══ */}
        <div className="therapist-cards-container">
          {ordered.map((medic) => {
            const row = scoreByMedic[medic.user_id];
            const units = row?.consult_units || 0;
            const norma = row?.norma_slots || 0;
            const cov = row?.coverage_percent || 0;
            const vac = row?.vacation_days || 0;
            const breakdown = row?.consult_breakdown || {};
            const hasConsults = Object.keys(breakdown).length > 0;

            return (
              <div key={medic.user_id} className="therapist-card medic-card">
                <h3>
                  <img src={avatar} alt="avatar" className="user-icon" />
                  {medic.firstname} {medic.lastname}
                </h3>

                {/* Lista consultații efectuate (nu punctaje) */}
                {hasConsults ? (
                  <ul>
                    {Object.entries(breakdown).map(([key, entry]) => (
                      <li key={key}>
                        {typeMap[key] || key}:{" "}
                        <span className="consult-count">{entry.count}</span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="card-empty">
                    <em>Nu există consultații efectuate.</em>
                  </p>
                )}

                <div className="card-footer">
                  <div className="card-footer-row">
                    <span className="card-footer-label">Total consultații:</span>
                    <span className="card-footer-value card-footer-score">{units}</span>
                  </div>
                  <div className="card-footer-row">
                    <span className="card-footer-label">Acoperire:</span>
                    <span className="card-footer-value">
                      {cov}%
                      <span className="card-footer-detail"> ({units}/{norma})</span>
                    </span>
                  </div>
                  {vac > 0 && (
                    <div className="card-footer-row">
                      <span className="card-footer-label">Concediu:</span>
                      <span className="card-footer-value">
                        {vac} {vac === 1 ? "zi" : "zile"}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default MedicScoreTable;